import { useState, useEffect } from "react";
import { Link, useLocation } from "react-router-dom";
import { Helmet } from "react-helmet-async";
import { Button } from "@/components/ui/button";
import api, { BACKEND_URL } from "@/lib/api";
import { toast } from "sonner";
import SharedNavigation from "@/components/SharedNavigation";
import LiveNowStrip from "@/components/LiveNowStrip";
import LivePromoCard from "@/components/LivePromoCard";
import NowOnlineStrip from "@/components/NowOnlineStrip";
import CommunityPulseWidget from "@/components/CommunityPulseWidget";
import HomeHeroCarousel from "@/components/HomeHeroCarousel";
import FeatureBoostChip from "@/components/FeatureBoostChip";
import ActivityFeedRail from "@/components/ActivityFeedRail";
import { BadgeWithModal } from "@/components/BadgeWithModal";
import { getCoverUrl as getTrackCoverUrl, getPlaylistCoverUrl, getProfilePhotoUrl, optimizeImage } from "@/lib/mediaUrls";
import { getDisplayPlaysForArtist, getDisplayPlays, formatDisplayPlays } from "@/lib/promoPlays";
import { PlayButton } from "@/components/player/PlayButton";
import { SinglesLockBadge, isSinglesForSale } from "@/components/singles/SinglesLockBadge";
import CollaboratorChips from "@/components/CollaboratorChips";
import { evolveBadges } from "@/lib/badges";
import FollowingFeed from "@/components/FollowingFeed";
import FeaturedBoard from "@/components/FeaturedBoard";
import MostPushedToday from "@/components/MostPushedToday";
import BeatsFeed from "@/components/BeatsFeed";
import ReelsTabEntry from "@/components/ReelsTabEntry";
import RGMReportsRail from "@/components/RGMReportsRail";

const getCoverUrl = (item) => {
  if (!item) return null;
  // Legacy callers may pass the bare filename string — return null so the
  // branded placeholder / <img onError> fallback kicks in (no ephemeral URL).
  if (typeof item === "string") return null;
  // Playlist shape uses cover_image; track/submission shape uses cover_url.
  if (item.cover_image && !item.cover_url) return getPlaylistCoverUrl(item);
  return getTrackCoverUrl(item);
};

const getAvatarUrl = (user) => {
  if (!user) return null;
  // Both track-enriched shape (artist_profile_photo/artist_picture) and user shape (profile_photo/picture)
  const compat = {
    ...user,
    profile_photo: user.profile_photo || user.artist_profile_photo,
    picture: user.picture || user.artist_picture,
  };
  return getProfilePhotoUrl(compat);
};

const FallbackCover =
  "https://images.unsplash.com/photo-1493225457124-a3eb161ffa5f?w=400&h=400&fit=crop";
const FallbackAvatar =
  "https://images.unsplash.com/photo-1535713875002-d1d0cf377fde?w=200&h=200&fit=crop";

// Section wrapper — consistent title + View All
const Section = ({ title, emoji, subtitle, viewAllTo, children, testId }) => (
  <section className="mb-14" data-testid={testId}>
    <div className="flex items-end justify-between mb-5 gap-4">
      <div>
        <h2 className="text-2xl md:text-3xl font-black text-white flex items-center gap-3 leading-none">
          {emoji && <span className="text-2xl md:text-3xl">{emoji}</span>}
          {title}
        </h2>
        {subtitle && (
          <p className="text-sm text-gray-400 mt-2">{subtitle}</p>
        )}
      </div>
      {viewAllTo && (
        <Link
          to={viewAllTo}
          className="text-sm font-semibold text-purple-300 hover:text-purple-200 whitespace-nowrap"
          data-testid={`${testId}-view-all`}
        >
          View All →
        </Link>
      )}
    </div>
    {children}
  </section>
);

// Horizontal scroll row
const Row = ({ children }) => (
  <div className="flex gap-4 overflow-x-auto pb-4 -mx-4 px-4 snap-x snap-mandatory scrollbar-thin scrollbar-thumb-purple-900 scrollbar-track-transparent">
    {children}
  </div>
);

// ========== Collab pill helpers (shared) ==========
// Renders the Featured / Collab / "feat. X" trio on any discovery card so the
// win-win visibility carries over from artist profiles into Discover/Charts.
const collabPillsFor = (track) => {
  const fa = Array.isArray(track.featured_artists) ? track.featured_artists : [];
  const isCollab = fa.length > 0;
  const isFeatured = track.status === "featured";
  const featuringNames = fa
    .map((f) => f.artist_name || f.name)
    .filter(Boolean);
  return { isCollab, isFeatured, featuringNames };
};

const CardPills = ({ track, compact = false }) => {
  const { isCollab, isFeatured, featuringNames } = collabPillsFor(track);
  // Phase 7.1 — Fan Boost visibility chips. Show 🔥 Fan Boosted whenever
  // a track has gifts; show 🎁 X coins on cards that already surface a
  // weekly/lifetime coin total; show 🏆 Most Supported only on the
  // "Most Supported This Week" rail (track.is_most_supported_week flag).
  const isFanBoosted = !!track.is_fan_boosted || (track.gift_score || 0) > 0;
  const showCoinsChip = (track.weekly_coins || 0) > 0
    || (track.recent_coins || 0) > 0
    || (track.gift_score || 0) >= 50;
  const coinValue = track.weekly_coins || track.recent_coins || track.gift_score || 0;
  const isMostSupportedWeek = !!track.is_most_supported_week;

  if (!isCollab && !isFeatured && !isFanBoosted) return null;
  return (
    <div className={`flex flex-wrap gap-1 ${compact ? "mt-1.5" : "mt-2"}`}>
      {isMostSupportedWeek && (
        <span
          className="px-1.5 py-0.5 bg-gradient-to-r from-yellow-400 to-amber-500 text-black text-[10px] font-bold rounded-full"
          data-testid={`pill-most-supported-${track.submission_id}`}
          title="Most Supported This Week"
        >
          🏆 Most Supported
        </span>
      )}
      {isFanBoosted && !isMostSupportedWeek && (
        <span
          className="px-1.5 py-0.5 bg-gradient-to-r from-rose-500/30 to-orange-500/30 text-orange-100 border border-orange-400/40 text-[10px] font-bold rounded-full"
          data-testid={`pill-fan-boosted-${track.submission_id}`}
          title="Fan Boosted by virtual gifts"
        >
          🔥 Fan Boosted
        </span>
      )}
      {showCoinsChip && coinValue > 0 && (
        <span
          className="px-1.5 py-0.5 bg-amber-500/20 text-amber-100 border border-amber-400/40 text-[10px] font-bold rounded-full"
          data-testid={`pill-coins-${track.submission_id}`}
          title={`${coinValue.toLocaleString()} coins received`}
        >
          🎁 {coinValue.toLocaleString()}
        </span>
      )}
      {isFeatured && (
        <span
          className="px-1.5 py-0.5 bg-gradient-to-r from-yellow-400 to-amber-500 text-black text-[10px] font-bold rounded-full"
          data-testid={`pill-featured-${track.submission_id}`}
          title="RGM Featured"
        >
          🎖️ Featured
        </span>
      )}
      {isCollab && (
        <span
          className="px-1.5 py-0.5 bg-pink-500/20 text-pink-200 border border-pink-500/30 text-[10px] font-bold rounded-full"
          title={
            featuringNames.length
              ? `with ${featuringNames.join(", ")}`
              : "Collaboration"
          }
          data-testid={`pill-collab-${track.submission_id}`}
        >
          🤝 Collab
        </span>
      )}
      {isCollab && featuringNames.length > 0 && !compact && (
        <span
          className="px-1.5 py-0.5 bg-pink-500/10 text-pink-300 text-[10px] rounded-full truncate max-w-[140px]"
          title={`with ${featuringNames.join(", ")}`}
          data-testid={`pill-feat-${track.submission_id}`}
        >
          feat. {featuringNames.slice(0, 1).join(", ")}
          {featuringNames.length > 1 ? `, +${featuringNames.length - 1}` : ""}
        </span>
      )}
    </div>
  );
};

// ========== Trending Track Card ==========
const TrendingTrackCard = ({ track, rank }) => {
  // Phase 9.7.19 — UX polish. For featured/spotlight tracks, the reach
  // meter does the heavy lifting on the cover; surfacing a tiny ▶ play
  // count next to "10K reach" reads as broken. Hide the literal play
  // pill on featured tracks (the play_count is still visible inside
  // TrackPage for transparency).
  const isPushed = track.status === "featured" || track.status === "spotlight";
  return (
  <Link
    to={`/track/${track.submission_id}`}
    className={`group relative flex-shrink-0 w-56 snap-start bg-gradient-to-br from-gray-900/80 to-black border rounded-xl overflow-hidden transition-all ${
      isPushed
        ? "border-amber-400/50 hover:border-amber-300/80 shadow-[0_0_24px_-4px_rgba(245,158,11,0.45)] hover:shadow-[0_0_30px_-2px_rgba(245,158,11,0.65)]"
        : "border-purple-500/20 hover:border-purple-500/60"
    }`}
    data-testid={`trending-track-${track.submission_id}`}
  >
    <div className="relative aspect-square overflow-hidden">
      <img
        src={getCoverUrl(track) || FallbackCover}
        alt={track.track_title}
        className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
        onError={(e) => {
          e.target.src = FallbackCover;
        }}
      />
      <div className="absolute top-2 left-2 px-2 py-1 rounded-md bg-black/80 text-orange-400 font-black text-sm border border-orange-500/40">
        #{rank}
      </div>
      <PlayButton track={track} sourceLabel="Trending" className="absolute top-2 right-2 opacity-80 group-hover:opacity-100" size={36} testIdSuffix="trending" />
      {isSinglesForSale(track) && (
        <SinglesLockBadge
          track={track}
          variant="card"
          className="absolute top-2 left-2 mt-8"
          testIdSuffix={`trending-${track.submission_id}`}
        />
      )}      {!isPushed && (
        <div
          className="absolute bottom-2 right-2 px-2 py-1 rounded-full bg-black/80 text-white text-xs font-medium flex items-center gap-1"
          title="Plays (real + promotional)"
          data-testid={`track-plays-${track.submission_id}`}
        >
          <span>▶</span>
          {formatDisplayPlays(getDisplayPlays(track))}
        </div>
      )}
      {/* Phase 9.7.6 — Feature/Spotlight surge meter overlay */}
      <FeatureBoostChip
        submission={track}
        variant="meter"
        className="absolute bottom-2 left-2 shadow-lg"
      />
    </div>
    <div className="p-3">
      <p className="font-bold text-white text-sm truncate">
        {track.track_title || "Untitled"}
      </p>
      <p className="text-purple-300 text-xs truncate mt-0.5">
        {track.display_artist_name || track.artist_name}
      </p>
      {Array.isArray(track.featured_artists) && track.featured_artists.length > 0 && (
        <CollaboratorChips
          featuredArtists={track.featured_artists}
          size="sm"
          maxVisible={3}
          className="mt-1.5"
          testIdSuffix={`trending-${track.submission_id}`}
        />
      )}
      <CardPills track={track} />
      {track.artist_badges && track.artist_badges.length > 0 && (
        <div className="flex gap-1 mt-2 flex-wrap">
          {evolveBadges(track.artist_badges).slice(0, 2).map((b) => (
            <BadgeWithModal key={b} type={b} size="small" showLabel={false} />
          ))}
        </div>
      )}
    </div>
  </Link>
  );
};

// ========== Trending Now Card (🔥 reaction-driven, recency-weighted) ==========
const TrendingNowCard = ({ track }) => {
  const fire = track.fire_count || 0;
  const total = track.reaction_total || fire;
  const isHot = fire >= 5; // visual emphasis cue
  return (
    <Link
      to={`/track/${track.submission_id}`}
      className="group relative flex-shrink-0 w-52 snap-start bg-gradient-to-br from-orange-900/40 via-rose-950/40 to-black border border-orange-500/30 rounded-xl overflow-hidden hover:border-orange-400/70 transition-all"
      data-testid={`trending-now-${track.submission_id}`}
    >
      <div className="relative aspect-square overflow-hidden">
        <img
          src={getCoverUrl(track) || FallbackCover}
          alt={track.track_title}
          className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
          onError={(e) => {
            e.target.src = FallbackCover;
          }}
        />
        {/* Live 🔥 chip — animates subtly to feel dynamic */}
        <div
          className={`absolute top-2 left-2 px-2 py-1 rounded-full bg-black/85 text-orange-300 font-bold text-xs flex items-center gap-1 border ${
            isHot ? "border-orange-400/70 animate-pulse" : "border-orange-500/40"
          }`}
        >
          <span>🔥</span>
          <span data-testid={`trending-now-fire-${track.submission_id}`}>
            {fire}
          </span>
        </div>
        <PlayButton track={track} sourceLabel="Trending Now" className="absolute top-2 right-2 opacity-80 group-hover:opacity-100" size={36} testIdSuffix="trending-now" />
        {isSinglesForSale(track) && (
          <SinglesLockBadge track={track} variant="card" className="absolute top-2 left-2" testIdSuffix={`trending-now-${track.submission_id}`} />
        )}
        {total > fire && (
          <div className="absolute bottom-2 right-2 px-2 py-1 rounded-full bg-black/80 text-white text-[10px] font-medium">
            {total} reactions
          </div>
        )}
        {/* Phase 9.7.6 — Feature/Spotlight surge meter overlay */}
        <FeatureBoostChip
          submission={track}
          variant="meter"
          className="absolute bottom-2 left-2 shadow-lg"
        />
      </div>
      <div className="p-3">
        <p className="font-bold text-white text-sm truncate">
          {track.track_title || "Untitled"}
        </p>
        <p className="text-orange-200 text-xs truncate mt-0.5">
          {track.display_artist_name || track.artist_name}
        </p>
        {Array.isArray(track.featured_artists) && track.featured_artists.length > 0 && (
          <CollaboratorChips
            featuredArtists={track.featured_artists}
            size="sm"
            maxVisible={3}
            className="mt-1.5"
            testIdSuffix={`trending-now-${track.submission_id}`}
          />
        )}
        <CardPills track={track} compact />
      </div>
    </Link>
  );
};

// ========== Phase 9.7.7 — Featured Push Today Card ==========
// Distinct gold→pink→purple gradient + animated "PUSHED BY RGM" badge
// so featured/spotlight tracks at the top of /home read instantly as
// "currently being promoted".
const FeaturedPushCard = ({ track }) => {
  const isSpotlight = track.status === "spotlight";
  // Phase 9.7.19 — Recently featured (<24h) gets the louder surge ring +
  // "FEATURE SURGE ACTIVE" badge so the card reads as alive instead of
  // "1 play" sleepy.
  const ageMs = track.featured_at ? Date.now() - new Date(track.featured_at).getTime() : Infinity;
  const isFresh = ageMs >= 0 && ageMs < 24 * 60 * 60 * 1000;
  return (
    <Link
      to={`/track/${track.submission_id}`}
      className={`group relative flex-shrink-0 w-56 snap-start rounded-2xl overflow-hidden border transition-all hover:-translate-y-0.5 ${
        isSpotlight
          ? "border-purple-400/60 hover:border-amber-300/80 shadow-lg shadow-purple-500/25"
          : "border-amber-400/60 hover:border-amber-300/80 shadow-lg shadow-amber-500/25"
      } ${isFresh ? "rgm-feature-surge-ring" : ""}`}
      style={{
        background: isSpotlight
          ? "linear-gradient(135deg, rgba(168,85,247,0.30), rgba(245,158,11,0.22) 50%, rgba(236,72,153,0.30))"
          : "linear-gradient(135deg, rgba(245,158,11,0.28), rgba(236,72,153,0.20) 50%, rgba(168,85,247,0.28))",
      }}
      data-testid={`featured-push-${track.submission_id}`}
    >
      <div className="relative aspect-square overflow-hidden">
        <img
          src={getCoverUrl(track) || FallbackCover}
          alt={track.track_title}
          className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
          onError={(e) => { e.target.src = FallbackCover; }}
        />
        {/* Animated PUSHED-BY-RGM ribbon */}
        <div
          className={`absolute top-2 left-2 px-2 py-0.5 rounded-full text-[10px] font-black uppercase tracking-widest text-white shadow-md ${
            isSpotlight
              ? "bg-gradient-to-r from-purple-500 to-amber-400 animate-pulse"
              : "bg-gradient-to-r from-amber-500 to-pink-500 animate-pulse"
          }`}
        >
          {isSpotlight ? "🌟 Spotlight Push" : "🚀 Featured Push"}
        </div>
        {isFresh && (
          <span
            className="absolute top-2 right-2 px-2 py-0.5 rounded-full text-[9px] font-black uppercase tracking-widest bg-black/70 text-amber-200 border border-amber-300/40 animate-pulse"
            title="Recently featured — surge active"
            data-testid={`feature-surge-${track.submission_id}`}
          >
            ⚡ Surge
          </span>
        )}
        <PlayButton track={track} sourceLabel={isSpotlight ? "Spotlight" : "Featured"} className="absolute bottom-2 right-2 shadow-xl" size={42} testIdSuffix="featured-push" />
        {isSinglesForSale(track) && (
          <SinglesLockBadge track={track} variant="card" className="absolute top-2 left-2" testIdSuffix={`featured-${track.submission_id}`} />
        )}
        {/* Boost meter (reach) */}
        <FeatureBoostChip
          submission={track}
          variant="meter"
          className="absolute bottom-2 left-2 shadow-lg"
        />
      </div>
      <div className="p-3 bg-black/35 backdrop-blur-sm">
        <p className="font-bold text-white text-sm truncate">
          {track.track_title || "Untitled"}
        </p>
        <p className={`text-xs truncate mt-0.5 ${isSpotlight ? "text-purple-200" : "text-amber-200"}`}>
          {track.artist_display_name || track.display_artist_name || track.artist_name || "RGM Artist"}
        </p>
        {Array.isArray(track.featured_artists) && track.featured_artists.length > 0 && (
          <CollaboratorChips
            featuredArtists={track.featured_artists}
            size="sm"
            maxVisible={2}
            className="mt-1.5"
            testIdSuffix={`featured-push-${track.submission_id}`}
          />
        )}
        <p className="text-[10px] text-amber-200/70 mt-1 uppercase tracking-wider font-bold">
          {isSpotlight ? "Promoted by RGM Studio" : "Currently in rotation"}
        </p>
      </div>
    </Link>
  );
};

// ========== New Drop Card ==========
const NewDropCard = ({ track, queue }) => (
  <Link
    to={`/track/${track.submission_id}`}
    className="group flex-shrink-0 w-48 snap-start"
    data-testid={`new-drop-${track.submission_id}`}
  >
    <div className="relative aspect-square rounded-xl overflow-hidden border border-green-500/20 group-hover:border-green-500/60 transition-all">
      <img
        src={getCoverUrl(track) || FallbackCover}
        alt={track.track_title}
        className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
        onError={(e) => {
          e.target.src = FallbackCover;
        }}
      />
      <div className="absolute top-2 left-2 px-2 py-0.5 rounded-full bg-green-500 text-black font-bold text-[10px] uppercase tracking-wider">
        New
      </div>
      <PlayButton track={track} queue={queue} sourceLabel="New Drops" className="absolute bottom-2 right-2 opacity-90" size={34} testIdSuffix="new-drop" />
      {isSinglesForSale(track) && (
        <SinglesLockBadge track={track} variant="card" className="absolute top-2 left-2" testIdSuffix={`new-drop-${track.submission_id}`} />
      )}
    </div>
    <p className="font-semibold text-white text-sm truncate mt-2 group-hover:text-green-300 transition-colors">
      {track.track_title || "Untitled"}
    </p>
    <p className="text-gray-400 text-xs truncate">
      {track.display_artist_name || track.artist_name}
    </p>
    {Array.isArray(track.featured_artists) && track.featured_artists.length > 0 && (
      <CollaboratorChips
        featuredArtists={track.featured_artists}
        size="sm"
        maxVisible={3}
        className="mt-1.5"
        testIdSuffix={`new-drop-${track.submission_id}`}
      />
    )}
    <CardPills track={track} compact />
  </Link>
);

// ========== Spotlight Winner Card (podium style) ==========
const SpotlightPodium = ({ winners, weekLabel }) => {
  const medals = { 1: "🥇", 2: "🥈", 3: "🥉" };
  return (
    <div className="relative bg-gradient-to-br from-amber-900/30 via-yellow-800/20 to-black border border-yellow-500/30 rounded-2xl p-6 md:p-8 overflow-hidden">
      <div className="absolute top-0 right-0 w-64 h-64 bg-yellow-500/10 blur-3xl rounded-full pointer-events-none" />
      <div className="flex items-center justify-between flex-wrap gap-3 mb-6 relative">
        <div>
          <p className="text-xs uppercase tracking-widest text-yellow-300 font-bold">
            Weekly Spotlight
          </p>
          <p className="text-sm text-gray-300 mt-1">
            {weekLabel || "This week's winners"}
          </p>
        </div>
        <Link
          to="/how-to-get-featured"
          className="text-xs text-yellow-300 hover:text-yellow-200 font-semibold"
        >
          How to win →
        </Link>
      </div>
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4 relative">
        {winners.map((w) => (
          <Link
            key={w.submission_id}
            to={`/track/${w.submission_id}`}
            className="group relative flex items-start gap-3 p-4 rounded-xl bg-black/40 border border-yellow-500/20 hover:border-yellow-500/60 transition-all"
            data-testid={`spotlight-winner-${w.submission_id}`}
          >
            <div className="relative flex-shrink-0">
              <img
                src={getCoverUrl(w) || FallbackCover}
                alt={w.track_title}
                className="w-20 h-20 rounded-lg object-cover"
                onError={(e) => {
                  e.target.src = FallbackCover;
                }}
              />
              <span className="absolute -top-2 -left-2 text-3xl drop-shadow-lg">
                {medals[w.position] || "🎖"}
              </span>
            </div>
            <div className="min-w-0 flex-1">
              <p className="font-bold text-white truncate">
                {w.track_title || "Untitled"}
              </p>
              <p className="text-yellow-300 text-sm truncate">
                {w.display_artist_name || w.artist_name}
              </p>
              {w.artist_badges && w.artist_badges.length > 0 && (
                <div className="flex gap-1 mt-1.5 flex-wrap">
                  {evolveBadges(w.artist_badges).slice(0, 2).map((b) => (
                    <BadgeWithModal key={b} type={b} size="small" showLabel={false} />
                  ))}
                </div>
              )}
            </div>
          </Link>
        ))}
      </div>
    </div>
  );
};

// ========== Playlist Card ==========
const PlaylistCard = ({ playlist }) => (
  <Link
    to={`/playlist/${playlist.playlist_id}`}
    className="group flex-shrink-0 w-56 snap-start bg-gradient-to-br from-cyan-900/30 to-black border border-cyan-500/20 rounded-xl overflow-hidden hover:border-cyan-500/60 transition-all"
    data-testid={`playlist-pick-${playlist.playlist_id}`}
  >
    <div className="relative aspect-square overflow-hidden">
      <img
        src={getCoverUrl(playlist) || FallbackCover}
        alt={playlist.name}
        className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
        onError={(e) => {
          e.target.src = FallbackCover;
        }}
      />
      <div className="absolute inset-0 bg-gradient-to-t from-black/80 via-transparent to-transparent" />
      <div className="absolute bottom-2 left-2 px-2 py-1 rounded-md bg-cyan-500/90 text-black font-bold text-xs flex items-center gap-1">
        <span>🎵</span>
        {playlist.track_count} tracks
      </div>
    </div>
    <div className="p-3">
      <p className="font-bold text-white text-sm truncate">{playlist.name}</p>
      <p className="text-cyan-300 text-xs truncate mt-0.5">
        {playlist.creator?.artist_name || playlist.creator?.name || "RGM Studio"}
      </p>
    </div>
  </Link>
);

// ========== Rising Artist Card ==========
const RisingArtistCard = ({ artist }) => (
  <Link
    to={`/artist/${artist.user_id}`}
    className="group flex-shrink-0 w-56 snap-start bg-gradient-to-br from-green-900/30 to-black border border-green-500/20 rounded-xl p-4 hover:border-green-500/60 transition-all"
    data-testid={`rising-artist-${artist.user_id}`}
  >
    <div className="flex flex-col items-center text-center">
      <div className="relative">
        <img
          src={optimizeImage(getAvatarUrl(artist) || FallbackAvatar, 'avatar')}
          alt={artist.artist_name || artist.name}
          className="w-20 h-20 rounded-full object-cover border-2 border-green-400/50 group-hover:border-green-400 transition-colors"
          onError={(e) => {
            e.target.src = FallbackAvatar;
          }}
        />
        <span className="absolute -bottom-1 -right-1 w-7 h-7 rounded-full bg-gradient-to-br from-green-400 to-emerald-500 flex items-center justify-center text-sm border-2 border-black">
          📈
        </span>
      </div>
      <p className="font-bold text-white text-sm mt-3 truncate max-w-full">
        {artist.artist_name || artist.name}
      </p>
      <div className="flex gap-3 text-xs text-gray-400 mt-1.5">
        <span title="Total plays (real + promotional)">{getDisplayPlaysForArtist(artist).toLocaleString()} plays</span>
        <span>•</span>
        <span>{artist.follower_count} followers</span>
      </div>
      {artist.collab_count > 0 && (
        <span
          className="mt-2 inline-flex items-center gap-1 px-1.5 py-0.5 rounded-full bg-pink-500/15 text-pink-200 border border-pink-500/30 text-[10px] font-semibold"
          title={`${artist.collab_count} collaboration${artist.collab_count === 1 ? '' : 's'}`}
          data-testid={`rising-artist-collab-${artist.user_id}`}
        >
          🤝 {artist.collab_count}
        </span>
      )}
      {artist.badges && artist.badges.length > 0 && (
        <div className="flex gap-1 mt-2 flex-wrap justify-center">
          {artist.badges.slice(0, 2).map((b) => (
            <BadgeWithModal key={b} type={b} size="small" showLabel={false} />
          ))}
        </div>
      )}
    </div>
  </Link>
);

// ========== Top Artist (rank list) ==========
// RGM Charts R1: renders a single row of the ranking rail. Backwards
// compatible with the legacy `artist_score` shape — new callers pass
// `rgm_rank_score`, which takes precedence.
const TopArtistRow = ({ artist, rank }) => {
  const score = Number.isFinite(artist?.rgm_rank_score)
    ? artist.rgm_rank_score
    : (artist?.artist_score || 0);
  const isRgm = Number.isFinite(artist?.rgm_rank_score);
  return (
  <Link
    to={`/artist/${artist.user_id}`}
    className="flex items-center gap-4 p-3 rounded-xl bg-gradient-to-r from-purple-900/20 to-transparent border border-purple-500/10 hover:border-purple-500/50 hover:from-purple-900/40 transition-all"
    data-testid={`top-artist-${artist.user_id}`}
  >
    <div className="w-10 text-center font-black text-2xl text-purple-300 flex-shrink-0">
      #{rank}
    </div>
    <img
      src={optimizeImage(getAvatarUrl(artist) || FallbackAvatar, 'avatar')}
      alt={artist.artist_name || artist.name}
      className="w-14 h-14 rounded-full object-cover border-2 border-purple-500/30 flex-shrink-0"
      onError={(e) => {
        e.target.src = FallbackAvatar;
      }}
    />
    <div className="min-w-0 flex-1">
      <p className="font-bold text-white truncate">
        {artist.artist_name || artist.name}
      </p>
      <div className="flex items-center gap-2 mt-0.5 flex-wrap">
        {isRgm ? (
          <>
            <span className="text-xs text-gray-400" title="Unique supporters in this period">
              {artist.unique_supporters || 0} supporters
            </span>
            <span className="text-xs text-gray-600">•</span>
            <span className="text-xs text-gray-400" title="Gifts received in this period">
              {artist.gifts_received || 0} gifts
            </span>
          </>
        ) : (
          <>
            <span className="text-xs text-gray-400" title="Total plays (real + promotional)">
              {getDisplayPlaysForArtist(artist).toLocaleString()} plays
            </span>
            <span className="text-xs text-gray-600">•</span>
            <span className="text-xs text-gray-400">
              {artist.follower_count} followers
            </span>
          </>
        )}
        {artist.collab_count > 0 && (
          <span
            className="inline-flex items-center gap-1 px-1.5 py-0.5 rounded-full bg-pink-500/15 text-pink-200 border border-pink-500/30 text-[10px] font-semibold"
            title={`${artist.collab_count} collaboration${artist.collab_count === 1 ? '' : 's'}`}
            data-testid={`top-artist-collab-${artist.user_id}`}
          >
            🤝 {artist.collab_count}
          </span>
        )}
      </div>
      {artist.badges && artist.badges.length > 0 && (
        <div className="flex gap-1 mt-1.5 flex-wrap">
          {evolveBadges(artist.badges).slice(0, 3).map((b) => (
            <BadgeWithModal key={b} type={b} size="small" showLabel={false} />
          ))}
        </div>
      )}
    </div>
    <div className="text-right flex-shrink-0">
      <p className="text-lg font-black text-white" data-testid={`top-artist-score-${artist.user_id}`}>
        {score.toLocaleString()}
      </p>
      <p className="text-[10px] uppercase tracking-widest text-purple-300">
        {isRgm ? 'RGM Score' : 'Score'}
      </p>
    </div>
  </Link>
  );
};

// ========== Main Page ==========
const DiscoveryHomePage = () => {
  const location = useLocation();
  // 🏠 When mounted at /home, the page wears its "Home" hat: different
  // hero copy, the Activity rail at the top, and a 60s auto-refresh of
  // the discover feed so it always feels live.
  const isHomeMode = location.pathname.startsWith("/home");
  // 🏷️ Discover tabs — segmented Featured / For You / Following / Beats
  // (Beats is a future-ready placeholder). Persisted in URL hash so a
  // refresh keeps the user on the tab they picked. Only active on
  // /discover; /home is the activity-driven feed.
  const initialTab = (() => {
    // Phase 9.7.3 — Show segmented tabs on BOTH /home and /discover so
    // the curated surfaces (Featured / Following / Beats) are always
    // one tap away. On /home we default to "for-you" so the Activity
    // rail + curated rails render immediately. On /discover the URL
    // hash preserves whichever tab the user last selected.
    const h = (location.hash || "").replace("#", "");
    if (["featured", "for-you", "following", "beats", "reels"].includes(h)) return h;
    return isHomeMode ? "for-you" : "featured";
  })();
  const [discoverTab, setDiscoverTab] = useState(initialTab);
  useEffect(() => {
    if (discoverTab && discoverTab !== "home") {
      window.history.replaceState(null, "", `#${discoverTab}`);
    }
  }, [discoverTab, isHomeMode]);
  const [currentUser, setCurrentUser] = useState(null);
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  // Phase 19.0 — Track the last fetch error so we can show a real "couldn't
  // load" banner with a retry CTA instead of the misleading "warming up"
  // empty-state. Production reports were showing this state even when the
  // platform had plenty of content.
  const [feedError, setFeedError] = useState(false);
  // Visibility-boost filter: when on, hide non-collab tracks across the
  // track-based sections so listeners can browse only collaborations.
  const [collabsOnly, setCollabsOnly] = useState(false);

  const loadDiscover = async (silent = false) => {
    if (!silent) setFeedError(false);
    // RGM Charts R1 — fetch the canonical weekly rankings alongside
    // /discover/home. The Home rail replaces the legacy top_artists_weekly
    // rendering with authoritative RGM Rank scores when available; falls
    // back silently to /discover/home's top_artists_weekly on failure.
    const [meRes, homeRes, rgmRes] = await Promise.allSettled([
      api.get("/auth/me"),
      api.get("/discover/home"),
      api.get("/charts/rankings?period=weekly&limit=10"),
    ]);
    if (meRes.status === "fulfilled") {
      setCurrentUser(meRes.value.data);
    }
    if (homeRes.status === "fulfilled") {
      const home = homeRes.value.data || {};
      if (rgmRes.status === "fulfilled" && Array.isArray(rgmRes.value.data?.artists)) {
        home.top_artists_weekly = rgmRes.value.data.artists;
        home.rgm_charts_period = rgmRes.value.data.period;
        home.rgm_charts_period_label = rgmRes.value.data.period_label;
      }
      setData(home);
      setFeedError(false);
    } else {
      setFeedError(true);
      if (!silent) {
        toast.error("Could not load discovery feed. Tap retry to try again.");
      }
    }
    setLoading(false);
  };

  useEffect(() => {
    loadDiscover();
    // 🏠 Home mode auto-refresh: every 60s, silently re-pull /discover/home
    // so the feed always feels fresh. Discover mode is one-shot.
    if (!isHomeMode) return;
    const id = setInterval(() => loadDiscover(true), 60_000);
    return () => clearInterval(id);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isHomeMode]);

  if (loading) {
    return (
      <div
        className="min-h-screen flex items-center justify-center"
        style={{ background: "#08080c" }}
      >
        <div className="inline-block animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-purple-500"></div>
      </div>
    );
  }

  const d = data || {};
  const onlyCollabs = (arr) =>
    (arr || []).filter(
      (t) => Array.isArray(t.featured_artists) && t.featured_artists.length > 0,
    );
  const trendingNow = collabsOnly
    ? onlyCollabs(d.trending_now)
    : d.trending_now || [];
  const trendingTracks = collabsOnly
    ? onlyCollabs(d.trending_tracks)
    : d.trending_tracks || [];
  const newDrops = collabsOnly
    ? onlyCollabs(d.new_artist_drops)
    : d.new_artist_drops || [];
  // Phase 7.1 Milestone D Part 2 — Fan Boost rails. The backend already
  // dedupes against trending_now / each other in /discover/home so we
  // don't refilter here.
  const mostSupportedWeek = collabsOnly
    ? onlyCollabs(d.most_supported_week)
    : d.most_supported_week || [];
  const fanBoosted = collabsOnly
    ? onlyCollabs(d.fan_boosted)
    : d.fan_boosted || [];
  // Phase 9.7.7 — 🚀 Featured Push Today rail. Surfaces currently
  // featured/spotlight tracks at the top of the For-You stack.
  const featuredPushToday = d.featured_push_today || [];
  const recentCollabs = d.recent_collabs || [];
  const risingFast = collabsOnly
    ? onlyCollabs(d.rising_fast)
    : d.rising_fast || [];
  const totalCollabs =
    onlyCollabs(d.trending_now).length +
    onlyCollabs(d.trending_tracks).length +
    onlyCollabs(d.new_artist_drops).length;
  const hasSpotlight =
    d.spotlight_winners &&
    d.spotlight_winners.winners &&
    d.spotlight_winners.winners.length > 0;

  return (
    <>
      <Helmet>
        {isHomeMode ? (
          <title>Home | RGM Studio</title>
        ) : (
          <title>Discover | RGM Studio</title>
        )}
        <meta
          name="description"
          content={isHomeMode
            ? "Live music & creator activity on RGM Studio — new track drops, fan boosts, trending beats, spotlight winners, and active live beams."
            : "Discover trending tracks, new artist drops, spotlight winners, playlist picks, rising artists, and this week's top artists on RGM Studio."}
        />
      </Helmet>
      <div className="min-h-screen bg-[#08080c]">
        <SharedNavigation currentUser={currentUser} activePage={isHomeMode ? "home" : "discover"} />

        {/* Hero Header — tighter padding in Home mode so the feed surfaces sooner */}
        <header className={`${isHomeMode ? "pt-20 pb-4" : "pt-24 pb-8"} px-4 sm:px-6 lg:px-8`}>
          <div className="max-w-7xl mx-auto flex flex-col sm:flex-row sm:items-end sm:justify-between gap-3">
            <div>
              <h1 className={`${isHomeMode ? "text-3xl sm:text-4xl" : "text-4xl sm:text-5xl lg:text-6xl"} font-black text-white tracking-tight`}>
                {isHomeMode ? "Home" : "Discover"}
              </h1>
              <p className="text-gray-400 mt-2 max-w-lg text-sm sm:text-base">
                {isHomeMode
                  ? "Live music & creator activity from your community."
                  : "Trending tracks, spotlight winners, rising stars, and the latest drops — all in one place."}
              </p>
            </div>
            <div className="flex flex-wrap items-center gap-2">
              <button
                type="button"
                onClick={() => setCollabsOnly((v) => !v)}
                aria-pressed={collabsOnly}
                title={
                  collabsOnly
                    ? "Showing only tracks with tagged collaborators"
                    : "Filter to only show collaboration tracks"
                }
                className={`inline-flex items-center gap-1.5 px-3 py-2 rounded-full text-sm font-semibold border transition-all whitespace-nowrap ${
                  collabsOnly
                    ? "bg-pink-500/20 text-pink-100 border-pink-400/60 shadow-[0_0_0_1px_rgba(244,114,182,0.4)]"
                    : "bg-gray-800/60 text-pink-200 border-pink-500/30 hover:bg-pink-500/10"
                }`}
                data-testid="discover-collabs-only-chip"
              >
                <span>🤝</span>
                <span>Collabs Only</span>
                {collabsOnly && totalCollabs > 0 && (
                  <span className="ml-1 px-1.5 py-0.5 rounded-full bg-black/40 text-[10px] text-pink-100">
                    {totalCollabs}
                  </span>
                )}
              </button>
            </div>
          </div>
        </header>

        <main className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 pb-20">
          {/* Phase 9.4 — Discover segmented tabs (Featured / For You /
              Following / Beats). Phase 9.7.3 — Now also visible on /home
              so curated tabs are always one tap away. */}
          {(
            <div
              className="flex gap-2 mb-6 overflow-x-auto pb-1 -mx-1 px-1 sticky top-[60px] sm:top-[68px] z-20 bg-[#08080c]/85 backdrop-blur-md"
              data-testid="discover-tabs"
            >
              {[
                { id: "featured",  label: "🏆 Featured" },
                { id: "for-you",   label: "🎧 For You" },
                { id: "following", label: "👥 Following" },
                { id: "reels",     label: "🎬 Reels" },
                { id: "beats",     label: "🎹 Beats" },
              ].map((t) => {
                const active = discoverTab === t.id;
                return (
                  <button
                    key={t.id}
                    type="button"
                    onClick={() => setDiscoverTab(t.id)}
                    className={`shrink-0 px-4 py-2 rounded-full text-sm font-bold border transition-all snap-start ${
                      active
                        ? "bg-gradient-to-r from-pink-500 to-purple-600 text-white border-pink-400 shadow-md shadow-pink-500/20"
                        : "bg-white/5 text-pink-200/80 border-white/10 hover:bg-white/10"
                    }`}
                    data-testid={`discover-tab-${t.id}`}
                  >
                    {t.label}
                  </button>
                );
              })}
            </div>
          )}

          {/* "Following" tab takes over the full main slot — its own grid */}
          {discoverTab === "following" && (
            <FollowingFeed />
          )}

          {/* 🏆 "Featured" tab — the official Feature Board. Replaces the
              curated rails so it feels like a dedicated showcase. */}
          {discoverTab === "featured" && (
            <FeaturedBoard />
          )}

          {/* Phase 15.5 P1 — Beats Marketplace MVP */}
          {discoverTab === "beats" && (
            <div data-testid="discover-tab-beats">
              <BeatsFeed />
            </div>
          )}

          {/* RGM Reels — Phase 1C. The Reels tab is a doorway, not an
              embedded feed, because the vertical reel viewer is a
              full-screen immersive surface (/reels). Keeping the
              preview card here matches the "Featured / For You /
              Following / Beats" tab pattern — a tap advances the
              listener into the dedicated experience. */}
          {discoverTab === "reels" && (
            <div data-testid="discover-tab-reels">
              <ReelsTabEntry />
            </div>
          )}

          {/* "For You" tab shows curated rails (Trending / Spotlight /
              Fresh Drops / etc.). Featured is now its own dedicated
              board above. Activity rail mounts on /home for-you only. */}
          {discoverTab === "for-you" && (
          <>
          {/* Phase 9.7.13 — 🎬 Home Hero / Spotlight Carousel.
              Renders only on /home (not /discover) so the catalog feel
              stays clean. Author slides via /admin/hero-slides. */}
          {isHomeMode && <HomeHeroCarousel />}
          {/* Phase 21.3a5 Batch 2 — 📡 RGM Studio Live Promo Card (admin-controlled) */}
          {isHomeMode && <LivePromoCard />}
          {/* 🎙️ Live Now strip — top 3 active rooms with auto-refresh */}
          <LiveNowStrip />
          {/* Phase 15.6M — 🟢 Now Online presence strip — artists currently Live / Online / Away / BRB */}
          {isHomeMode && <NowOnlineStrip />}

          {/* Phase 15.6P — 📊 Community Pulse Widget (live platform vitals) */}
          {isHomeMode && (
            <div className="mb-8">
              <CommunityPulseWidget />
            </div>
          )}

          {/* Phase 9.7.7 — 🚀 Featured Push Today rail. Sits ABOVE every
              other curated rail so featured/spotlight artists feel
              immediately + visibly promoted on every authed user's
              landing page. Hidden when empty. */}
          {featuredPushToday.length > 0 && (
            <Section
              title="Featured Push Today"
              emoji="🚀"
              subtitle="Tracks currently being promoted by RGM Studio — boosted reach + extra rotation"
              testId="section-featured-push-today"
            >
              <Row>
                {featuredPushToday.map((t) => (
                  <FeaturedPushCard key={t.submission_id} track={t} />
                ))}
              </Row>
            </Section>
          )}

          {/* Phase 9.7.16 — 🏆 Most Pushed Today leaderboard.
              Lives on /home only; renders its own header/section.
              Hides itself when empty. */}
          {isHomeMode && <MostPushedToday />}

          {/* 📰 Activity rail — only on /home (Discover stays catalog-style). */}
          {isHomeMode && <ActivityFeedRail />}

          {/* Fix 3 (Feb 2026) — the CommunityActivityFeed rail was removed
              from the For You / Home experience per operator request.
              The dedicated /community page keeps its own Activity Feed,
              and the same reusable component still ships on Creator Hub. */}

          {/* Phase 15.6A — RGM Reports rail (auto-hides if empty) */}
          {isHomeMode && <RGMReportsRail limit={5} />}

          {trendingNow.length > 0 && (
            <Section
              title={collabsOnly ? "🔥 Trending Now (Collabs)" : "🔥 Trending Now"}
              emoji=""
              subtitle="What the community is reacting to right now"
              testId="section-trending-now"
            >
              <Row>
                {trendingNow.map((t) => (
                  <TrendingNowCard key={t.submission_id} track={t} />
                ))}
              </Row>
            </Section>
          )}

          {/* 0.1 🏆 Most Supported This Week — last 7d gift coins.
              Backend dedupes against Trending Now so no card is shared. */}
          {mostSupportedWeek.length > 0 && (
            <Section
              title="Most Supported This Week"
              emoji="🏆"
              subtitle="Tracks the community is gifting hardest right now"
              viewAllTo="/leaderboard"
              testId="section-most-supported-week"
            >
              <Row>
                {mostSupportedWeek.map((t, i) => (
                  <TrendingTrackCard key={t.submission_id} track={t} rank={i + 1} />
                ))}
              </Row>
            </Section>
          )}

          {/* 0.2 ⚡ Rising Fast — last 48h gift + engagement momentum */}
          {risingFast.length > 0 && (
            <Section
              title="Rising Fast"
              emoji="⚡"
              subtitle="Big momentum in the last 48 hours"
              testId="section-rising-fast"
            >
              <Row>
                {risingFast.map((t) => (
                  <TrendingNowCard key={t.submission_id} track={t} />
                ))}
              </Row>
            </Section>
          )}

          {/* 0.3 🎁 Fan Boosted — any track with at least one gift */}
          {fanBoosted.length > 0 && (
            <Section
              title="Fan Boosted"
              emoji="🎁"
              subtitle="Tracks lifted by listeners using virtual gifts"
              viewAllTo="/leaderboard"
              testId="section-fan-boosted"
            >
              <Row>
                {fanBoosted.map((t, i) => (
                  <TrendingTrackCard key={t.submission_id} track={t} rank={i + 1} />
                ))}
              </Row>
            </Section>
          )}

          {/* Phase 9.7.5 — 🤝 Collabs rail. Always visible when there are
              tagged collaborations on the platform; gives collabs a
              guaranteed surface independent of trending/gift signals. */}
          {recentCollabs.length > 0 && (
            <Section
              title="Recent Collabs"
              emoji="🤝"
              subtitle="Fresh collaborations between RGM artists"
              testId="section-recent-collabs"
            >
              <Row>
                {recentCollabs.map((t) => (
                  <TrendingNowCard key={t.submission_id} track={t} />
                ))}
              </Row>
            </Section>
          )}

          {/* 1. Spotlight Winners (hidden when filtering collabs-only) */}
          {!collabsOnly && hasSpotlight && (
            <Section
              title="Spotlight Winners"
              emoji="🏆"
              subtitle="Top 3 picked by our A&R team each week"
              testId="section-spotlight-winners"
            >
              <SpotlightPodium
                winners={d.spotlight_winners.winners}
                weekLabel={d.spotlight_winners.week_label}
              />
            </Section>
          )}

          {/* 2. Trending Tracks */}
          {trendingTracks.length > 0 && (
            <Section
              title={collabsOnly ? "Trending Collabs" : "Trending Tracks"}
              emoji="🔥"
              subtitle={
                collabsOnly
                  ? "Hottest collaboration tracks right now"
                  : "What RGM listeners can't stop playing"
              }
              viewAllTo="/charts"
              testId="section-trending-tracks"
            >
              <Row>
                {trendingTracks.map((t, i) => (
                  <TrendingTrackCard key={t.submission_id} track={t} rank={i + 1} />
                ))}
              </Row>
            </Section>
          )}

          {/* 3. New Artist Drops */}
          {newDrops.length > 0 && (
            <Section
              title={collabsOnly ? "New Collab Drops" : "New Artist Drops"}
              emoji="✨"
              subtitle={
                collabsOnly
                  ? "Fresh collabs from the RGM community"
                  : "Fresh tracks from the RGM community"
              }
              viewAllTo="/explore"
              testId="section-new-artist-drops"
            >
              <Row>
                {newDrops.map((t) => (
                  <NewDropCard key={t.submission_id} track={t} queue={newDrops} />
                ))}
              </Row>
            </Section>
          )}

          {/* Sections below are artist/playlist-based — hidden under
              "Collabs Only" since they don't carry featured_artists. */}
          {!collabsOnly && d.playlist_picks && d.playlist_picks.length > 0 && (
            <Section
              title="Playlist Picks"
              emoji="🎵"
              subtitle="Curated by artists and listeners"
              viewAllTo="/playlists"
              testId="section-playlist-picks"
            >
              <Row>
                {d.playlist_picks.map((pl) => (
                  <PlaylistCard key={pl.playlist_id} playlist={pl} />
                ))}
              </Row>
            </Section>
          )}

          {/* 5. Rising Artists */}
          {!collabsOnly && d.rising_artists && d.rising_artists.length > 0 && (
            <Section
              title="Rising Artists"
              emoji="📈"
              subtitle="Breakthrough talent gaining momentum"
              viewAllTo="/charts"
              testId="section-rising-artists"
            >
              <Row>
                {d.rising_artists.map((a) => (
                  <RisingArtistCard key={a.user_id} artist={a} />
                ))}
              </Row>
            </Section>
          )}

          {/* 6. RGM Charts — This Week */}
          {!collabsOnly && d.top_artists_weekly && d.top_artists_weekly.length > 0 && (
            <Section
              title="RGM Charts — This Week"
              emoji="👑"
              subtitle={d.rgm_charts_period_label
                ? `RGM Rank Score · ${d.rgm_charts_period_label}`
                : "Ranked by RGM Rank Score"}
              viewAllTo="/charts?chart=top-artists&period=weekly"
              testId="section-top-artists-weekly"
            >
              <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                {d.top_artists_weekly.slice(0, 10).map((a, i) => (
                  <TopArtistRow
                    key={a.user_id}
                    artist={a}
                    rank={a.rank || i + 1}
                  />
                ))}
              </div>
            </Section>
          )}

          {/* Empty state for "Collabs Only" filter */}
          {collabsOnly && trendingNow.length === 0 && trendingTracks.length === 0 && newDrops.length === 0 && (
            <div
              className="text-center py-20 max-w-md mx-auto"
              data-testid="discover-no-collabs-state"
            >
              <div className="text-6xl mb-4">🤝</div>
              <p className="text-white text-lg font-semibold">
                No collaboration tracks here yet
              </p>
              <p className="text-gray-400 text-sm mt-2">
                Be the first — tag a featured artist on your next upload and
                land in this feed.
              </p>
              <button
                type="button"
                onClick={() => setCollabsOnly(false)}
                className="mt-5 inline-flex items-center gap-1.5 px-4 py-2 rounded-full bg-gray-800/60 text-pink-200 border border-pink-500/30 hover:bg-pink-500/10 text-sm"
                data-testid="discover-clear-collabs-filter"
              >
                Show all tracks
              </button>
            </div>
          )}

          {/* Empty state */}
          {!collabsOnly && !hasSpotlight &&
            (!d.trending_now || d.trending_now.length === 0) &&
            (!d.trending_tracks || d.trending_tracks.length === 0) &&
            (!d.new_artist_drops || d.new_artist_drops.length === 0) &&
            (!d.playlist_picks || d.playlist_picks.length === 0) &&
            (!d.rising_artists || d.rising_artists.length === 0) &&
            (!d.top_artists_weekly || d.top_artists_weekly.length === 0) && (
              <div
                className="text-center py-20"
                data-testid="discover-empty-state"
              >
                <div className="text-6xl mb-4">🎵</div>
                {feedError ? (
                  <>
                    <p className="text-white text-lg font-semibold">
                      We couldn't reach the discovery feed.
                    </p>
                    <p className="text-gray-400 text-sm mt-2">
                      Check your connection and try again.
                    </p>
                    <Button
                      onClick={() => { setLoading(true); loadDiscover(); }}
                      className="mt-5 bg-gradient-to-r from-purple-600 to-pink-600 text-white"
                      data-testid="discover-retry-button"
                    >
                      🔄 Retry
                    </Button>
                  </>
                ) : (
                  <>
                    <p className="text-gray-300 text-lg">
                      Nothing in your For You feed just yet.
                    </p>
                    <p className="text-gray-500 text-sm mt-2">
                      Follow a few artists and check back in a moment — we're curating fresh picks for you.
                    </p>
                    <div className="mt-5 flex flex-wrap gap-2 justify-center">
                      <Link to="/explore">
                        <Button className="bg-purple-600 hover:bg-purple-500 text-white" data-testid="discover-explore-button">
                          Explore artists
                        </Button>
                      </Link>
                      <Button
                        onClick={() => { setLoading(true); loadDiscover(); }}
                        variant="outline"
                        className="text-white border-gray-700"
                        data-testid="discover-refresh-button"
                      >
                        🔄 Refresh
                      </Button>
                    </div>
                  </>
                )}
              </div>
            )}
          </>
          )}
        </main>
      </div>
    </>
  );
};

export default DiscoveryHomePage;
